import React from 'react'
import SimpleDropdown from './DropDownView'
import { getTeamNames, updateTeamName } from '../model/request/quotes'
import { normalizeWhitespace } from '../model/TextNormalization'

// Shared across every screen that sets a team, so one request fills all the
// dropdowns on a page. The group-by-job view renders one picker per job row,
// which without this would be a dozen identical requests. Cleared after a
// save so a team typed via 'Other' shows up on the next mount.
let teamsPromise = null

// Label for the empty team. team_name defaults to '' and getTeamNames
// excludes it, so without this there is no way to read or clear "no team".
// A real team actually named "Unassigned" would collide with it - the same
// tradeoff the daily schedule's Team filter makes.
const UNASSIGNED = 'Unassigned'

/**
 * @return {Promise<[string]>}
 */
function fetchTeams() {
    if (!teamsPromise) {
        teamsPromise = getTeamNames().then((result) => result.data.teams)
    }
    return teamsPromise
}

/**
 * Team picker for one job. Owns the whole interaction - the option list, the
 * 'Other' free-text entry, the loader and the save - so that every screen
 * using it behaves identically.
 *
 * Props:
 *   quoteId   required
 *   value     optional, the *initial* team (see below)
 *   teams     optional, the option list; omitted means fetch it
 *   onChange  optional, called with the new value after a pick
 *   plus turnOnLoader / turnOffLoader / showError / showTextFieldModal,
 *   which every screen already has - spread `{...this.props}` in.
 *
 * Deliberately uncontrolled: the live selection lives in state, seeded from
 * `value`. The group-by-job view rebuilds its job objects from full_task on
 * every render, so a controlled component would snap back to the old team the
 * next time the parent re-rendered.
 */
class TeamDropdown extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            'team': props.value || '',
            'teams': props.teams === undefined ? [] : props.teams,
        }
    }

    componentDidMount() {
        // === undefined, not falsy: [] is a legitimate "no teams yet" and
        // must not trigger a fetch.
        if (this.props.teams === undefined) {
            fetchTeams().then(
                (teams) => this.setState({'teams': teams}),
                (error) => this.props.showError(error),
            )
        }
    }

    componentDidUpdate(prevProps) {
        // Only on an actual prop change - a refetch from the server landing,
        // not every re-render.
        if (prevProps.value !== this.props.value) {
            this.setState({'team': this.props.value || ''})
        }
        if (prevProps.teams !== this.props.teams &&
            this.props.teams !== undefined) {
            this.setState({'teams': this.props.teams})
        }
    }

    save(team) {
        this.setState({'team': team})
        if (this.props.onChange) {
            this.props.onChange(team)
        }
        // A new team from 'Other' will not be in the cached list.
        teamsPromise = null

        this.props.turnOnLoader()
        updateTeamName(this.props.quoteId, team).then(
            (result) => {
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    render() {
        const team = this.state.team
        const values = [...this.state.teams]
        if (team && values.indexOf(team) === -1) {
            values.push(team)
        }
        // Unassigned second to last, Other last. The off-list value above
        // goes in before both so that ordering holds. A job with no team
        // stores '', which would otherwise render as a blank toggle with
        // nothing to click to clear it again.
        values.push(UNASSIGNED)
        values.push('Other')

        return <SimpleDropdown
            index={values.indexOf(team === '' ? UNASSIGNED : team)}
            values={values}
            size={this.props.size}
            onChange={(index) => {
                if (values[index] === 'Other') {
                    this.props.showTextFieldModal('Team', (value) => {
                        this.save(normalizeWhitespace(value))
                    })
                } else {
                    this.save(values[index] === UNASSIGNED
                        ? ''
                        : values[index])
                }
            }} />
    }
}

export default TeamDropdown
